/**
 * Размерын карт — «Бүх гутал»-ын нүүр (2026-09-28).
 *
 * Өмнө нь хуудас БҮХ гутлыг 12-оор хуудаслаж жагсаадаг байв: «38 байна
 * уу» гэж асуухад ажилтан хуудас хуудсаар гүйлгэдэг байлаа. Одоо размер
 * бүр нэг карт — хэдэн гутал, хэд нь агуулахад, хэд нь салбарт; дарахад
 * зөвхөн тэр размерын гутал.
 *
 * ⚠️ Тоо нь размерын шүүлтүүрийг ТООЦОХГҮЙ (`GET /items/facets`) — 38-ыг
 *    сонгосон ч бусад карт тоотойгоо үлдэж, нэг товчоор шилжинэ.
 *
 * ⚠️ Зурвас нь ХАМГИЙН ОЛОН размертай харьцуулсан хэмжээ — аль размер
 *    зузаан, аль нь нимгэн байгааг нэг харцаар. Өнгө нь төлөв биш.
 */

import { compareSize, formatQty, formatSize, type SizeFacet } from '@boots/domain';
import { useEffect, useRef, type Ref } from 'react';

/** URL-ийн `size` утга: размергүй нь 'none'. */
const keyOf = (size: number | null) => (size === null ? 'none' : String(size));

const GRID = 'grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 2xl:grid-cols-10';

/**
 * Жагсаалт нээлттэй үед гар утсанд НЭГ мөр, хажуу тийш гүйлгэнэ —
 * 6 мөр карт жагсаалтыг дэлгэцээс түлхэж гаргадаг байв. `sm`-ээс дээш
 * сүлжээ хэвээр.
 */
const STRIP =
  'flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-4 sm:overflow-visible sm:pb-0 ' +
  'md:grid-cols-6 lg:grid-cols-8 2xl:grid-cols-10';

export function SizeGrid({ sizes, value, showSplit, onPick }: {
  sizes: SizeFacet[];
  /** '' | 'all' | 'none' | '38' — сонгогдсон карт. Сонгосон үед жагсаалт нээлттэй */
  value: string;
  /** Нэгж сонгоогүй үед л «агуулах · салбар» задаргаа утгатай */
  showSplit: boolean;
  onPick: (size: string) => void;
}) {
  // Гар утасны мөрөнд сонгосон карт харагдах хэсэгт гарна
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (value && window.matchMedia('(max-width: 639px)').matches) {
      activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  }, [value]);
  const cell = value ? 'w-28 shrink-0 sm:w-auto' : undefined;

  // Сонгосон размер шүүлтүүр солиход 0 болж болно — карт нь алга
  // болбол юу сонгогдсоныг харахгүй тул 0-тэйгээр үлдээнэ.
  const cards =
    value && value !== 'all' && !sizes.some((s) => keyOf(s.size) === value)
      ? [...sizes, { size: value === 'none' ? null : Number(value), count: 0, warehouse: 0, retail: 0 }]
          .sort((a, b) => compareSize(a.size, b.size))
      : sizes;

  const max = Math.max(1, ...sizes.map((s) => s.count));
  const sum = (k: 'count' | 'warehouse' | 'retail') => sizes.reduce((n, s) => n + s[k], 0);
  // 0-тэй хэсгийг бичихгүй — «агуулах 21» нь «бүгд агуулахад» гэдгийг хэлнэ
  const split = (w: number, r: number) =>
    showSplit
      ? [w > 0 ? `агуулах ${formatQty(w)}` : '', r > 0 ? `салбар ${formatQty(r)}` : '']
          .filter(Boolean).join(' · ') || null
      : null;

  return (
    <ul className={value ? STRIP : GRID}>
      <li className={cell}>
        <SizeCard
          ref={value === 'all' ? activeRef : undefined}
          label="Бүх размер"
          small
          count={sum('count')}
          split={split(sum('warehouse'), sum('retail'))}
          active={value === 'all'}
          onClick={() => onPick('all')}
        />
      </li>
      {cards.map((s) => (
        <li key={keyOf(s.size)} className={cell}>
          <SizeCard
            ref={value === keyOf(s.size) ? activeRef : undefined}
            label={s.size === null ? 'Размергүй' : formatSize(s.size)}
            small={s.size === null}
            count={s.count}
            split={split(s.warehouse, s.retail)}
            share={(s.count / max) * 100}
            active={value === keyOf(s.size)}
            onClick={() => onPick(keyOf(s.size))}
          />
        </li>
      ))}
    </ul>
  );
}

function SizeCard({ ref, label, small = false, count, split, share, active, onClick }: {
  ref?: Ref<HTMLButtonElement>;
  label: string;
  /** Урт шошго («Размергүй») — жижиг үсгээр, картаас халихгүй */
  small?: boolean;
  count: number;
  split: string | null;
  /** 0–100, хамгийн олон размертай харьцуулсан */
  share?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex h-full min-h-11 w-full flex-col gap-1 rounded-well border px-3 pt-2.5 pb-2
        text-left transition active:translate-y-[0.5px] ${
        active
          ? 'border-accent-700 bg-accent-100 text-accent-700'
          : 'border-hair bg-panel hover:border-accent-300 hover:bg-row-hover'
      }`}
    >
      <span className={`num leading-none ${small ? 'text-h3' : 'text-metric-sm'}`}>{label}</span>
      <span className="num text-meta">
        <span className="font-semibold">{formatQty(count)}</span> гутал
      </span>
      {split ? <span className="num text-micro text-ink-3 text-pretty">{split}</span> : null}
      {share === undefined ? null : (
        <span className="mt-auto h-1 overflow-hidden rounded-full bg-track" aria-hidden>
          <span className="block h-full rounded-full bg-accent-400" style={{ width: `${share}%` }} />
        </span>
      )}
    </button>
  );
}
